import React, { useState } from 'react';
import { X, ExternalLink, ChevronLeft, ChevronRight, Download } from 'lucide-react';
import { TaskImage } from '../types/Task';
import { taskApi } from '../services/api';
import './ImagePreviewModal.css';

interface ImagePreviewModalProps {
  images: TaskImage[];
  initialIndex?: number;
  onClose: () => void;
}

const ImagePreviewModal: React.FC<ImagePreviewModalProps> = ({
  images,
  initialIndex = 0,
  onClose
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [loading, setLoading] = useState(true);

  if (images.length === 0) return null;

  const currentImage = images[currentIndex];
  const imageUrl = taskApi.getImageUrl(currentImage.id);

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
    setLoading(true);
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
    setLoading(true);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    } else if (e.key === 'ArrowLeft') {
      handlePrevious();
    } else if (e.key === 'ArrowRight') {
      handleNext();
    }
  };

  const handleOpenInNewTab = () => {
    window.open(imageUrl, '_blank');
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = imageUrl;
    link.download = currentImage.original_name || 'image';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div 
      className="image-preview-modal-backdrop" 
      onClick={onClose}
      onKeyDown={handleKeyPress}
      tabIndex={0}
    >
      <div className="image-preview-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="image-preview-header">
          <div className="image-info">
            <h3>{currentImage.original_name}</h3>
            <span className="image-counter">
              {currentIndex + 1} of {images.length}
            </span>
          </div>
          <div className="image-actions">
            <button
              className="action-btn"
              onClick={handleOpenInNewTab}
              title="Open in new tab"
            >
              <ExternalLink size={16} />
            </button>
            <button
              className="action-btn"
              onClick={handleDownload}
              title="Download"
            >
              <Download size={16} />
            </button>
            <button
              className="action-btn close-btn"
              onClick={onClose}
              title="Close"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Image container */}
        <div className="image-container">
          {loading && (
            <div className="image-loading">
              <div className="loading-spinner"></div>
              <p>Loading image...</p>
            </div>
          )}
          
          <img
            src={imageUrl}
            alt={currentImage.original_name}
            className={`preview-image ${loading ? 'loading' : ''}`}
            onLoad={() => setLoading(false)}
            onError={() => setLoading(false)}
          />

          {/* Navigation arrows */}
          {images.length > 1 && (
            <>
              <button
                className="nav-arrow nav-arrow-left"
                onClick={handlePrevious}
                title="Previous image"
              >
                <ChevronLeft size={24} />
              </button>
              <button
                className="nav-arrow nav-arrow-right"
                onClick={handleNext}
                title="Next image"
              >
                <ChevronRight size={24} />
              </button>
            </>
          )}
        </div>

        {/* Thumbnail strip */}
        {images.length > 1 && (
          <div className="thumbnail-strip">
            {images.map((image, index) => (
              <div
                key={image.id}
                className={`thumbnail ${index === currentIndex ? 'active' : ''}`}
                onClick={() => {
                  setCurrentIndex(index);
                  setLoading(true);
                }}
              >
                <img
                  src={taskApi.getImageUrl(image.id)}
                  alt={image.original_name}
                  className="thumbnail-image"
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ImagePreviewModal; 